import React, { useEffect, useState } from "react";
import {
	IconButton,
	Tooltip,
	Typography,
	Link,
	Button,
	Grid,
	LinearProgress,
	Box,
} from "@mui/material";
import { useSelector } from "react-redux";

import { CommentIcon } from "utility/icons";
import { Shared } from "utility/shared";
import Emitter from "utility/emitter";
import ResponsiveDialog from "components/ResponsiveDialog";
import { RootState } from "redux/store";
import { IStepHistoryComment } from "models/StepHistoryDetail";
import { CustomTooltip } from "components/CustomTooltip";
import {
	IS_CONTRACT_ASSIGNED_TO_USER,
	OPEN_CONTRACT_STEP_HISTORY_DETAIL,
} from "utility/emitterEvents";
import {
	ContractStepHistoryEmitter,
	IContractStepHistory,
} from "models/ContractManagement/ContractStepHistoryEmitter";
import { ContractService } from "services/contractManagement.service";
import { ContractStepStatus } from "utility/enums/contract-detail";
import { Loading } from "components/Loading";
import { ContractManagementUtil } from "utility/contractManagementUtil";
import ContractAttachment from "../ContractAttachment";

type Props = {
	contractDetail: ContractStepHistoryEmitter;
};

const ContractStepsHistory: React.FC<Props> = ({ contractDetail }) => {
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);
	const [contractStepHistoryList, setContractStepHistoryList] = useState<
		IContractStepHistory[]
	>([]);
	const [openInfoDialog, setOpenInfoDialog] = useState<{
		message: string;
		open: boolean;
	}>({
		message: "",
		open: false,
	});
	const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isContractStepAssignedToUser = (step: IContractStepHistory) => {
		return step.assignToUsers && step.assignToUsers.length > 0
			? step.assignToUsers.findIndex((r) => r.userId === userDetails.userId) >
					-1
			: false;
	};

	const openContractStepHistoryDetail = (item: IContractStepHistory) => {
		Emitter.off(OPEN_CONTRACT_STEP_HISTORY_DETAIL, {} as any);
		Emitter.emit(OPEN_CONTRACT_STEP_HISTORY_DETAIL, item);
	};

	const setPrevStepAsSelected = (
		stepHistoryList: IContractStepHistory[],
		filteredList: IContractStepHistory[]
	) => {
		for (let i = 0; i < filteredList.length; i++) {
			const element = filteredList[i];
			if (isContractStepAssignedToUser(element)) {
				const index = stepHistoryList.findIndex(
					(r) => r.contractStepId === element.contractStepId
				);
				if (index > -1) {
					setActiveStepIndex(index);
					openContractStepHistoryDetail(stepHistoryList[index]);
					Emitter.emit(
						IS_CONTRACT_ASSIGNED_TO_USER,
						isContractStepAssignedToUser(stepHistoryList[index])
					);
					break;
				}
			}
			// if no prev step is assigned to user, set first step as selected
			if (filteredList.length - 1 === i) {
				setActiveStepIndex(0);
				openContractStepHistoryDetail(stepHistoryList[0]);
			}
		}
	};

	useEffect(() => {
		// get ContractStepHistoryList
		if (contractDetail.contractId > -1) {
			setIsLoading(true);
			setActiveStepIndex(-1);
			ContractService.getContractStepList(contractDetail.contractId)
				.then((result) => {
					setContractStepHistoryList(result);
					// groupWorkflowSteps(result);
					const index = result.findIndex(
						(r) => r.contractStepId === contractDetail.contractStepId
					);
					if (index > -1) {
						if (isContractStepAssignedToUser(result[index])) {
							setActiveStepIndex(index);
							Emitter.emit(
								IS_CONTRACT_ASSIGNED_TO_USER,
								isContractStepAssignedToUser(result[index])
							);
						} else {
							setPrevStepAsSelected(
								result,
								index > 0
									? JSON.parse(JSON.stringify(result)).slice(0, index).reverse()
									: JSON.parse(JSON.stringify(result)).reverse()
							);
						}
					} else {
						setPrevStepAsSelected(
							result,
							JSON.parse(JSON.stringify(result)).reverse()
						);
					}
					const activeElement = document.getElementById("active");
					if (activeElement) {
						activeElement.scrollIntoView({
							block: "center",
							behavior: "smooth",
						});
					}
					setIsLoading(false);
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error loading contract step history list, please try again"
					);
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contractDetail.contractId, contractDetail.contractStepId]);

	const getClassName = (status: string) => {
		let classes: string = "steps-root ";
		if (status === ContractStepStatus.Ready) {
			classes += "in-progress";
		}
		if (status === ContractStepStatus.Completed) {
			classes += "complete";
		}
		if (status === ContractStepStatus.Recalled) {
			classes += "recall";
		}
		if (status === ContractStepStatus.ChangeRequested) {
			classes += "change-request";
		}
		if (status === ContractStepStatus.Rejected) {
			classes += "reject";
		}
		if (status === ContractStepStatus.Deleted) {
			classes += "delete";
		}
		if (status === ContractStepStatus.Delegated) {
			classes += "delegate";
		}
		if (status === ContractStepStatus.Escalated) {
			classes += "escalate";
		}
		if (status === ContractStepStatus.Cancelled) {
			classes += "cancel";
		}
		return classes;
	};

	const handleStepDetailClick = (item: IContractStepHistory, index: number) => {
		if (activeStepIndex === index) {
			return;
		}
		if (
			item.stepStatus === ContractStepStatus.Pending ||
			(item.stepStatus === ContractStepStatus.Ready &&
				!isContractStepAssignedToUser(item))
		) {
			setOpenInfoDialog({
				open: true,
				message: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
			});
		} else {
			setActiveStepIndex(index);
			openContractStepHistoryDetail(item);
			Emitter.emit(
				IS_CONTRACT_ASSIGNED_TO_USER,
				isContractStepAssignedToUser(item)
			);
		}
	};

	const groupWorkflowSteps = (steps: IContractStepHistory[]) => {
		const grouped: { [key: string]: IContractStepHistory[] } = {};

		steps.forEach((step) => {
			const groupName =
				Object.entries(ContractManagementUtil.workflowGroupsConfig).find(
					([, ids]) => ids.includes(step.modulePhaseId)
				)?.[0] || "Other Workflow";

			if (!grouped[groupName]) grouped[groupName] = [];
			grouped[groupName].push(step);
		});

		return Object.entries(grouped).map(([type, steps]) => ({
			type,
			steps,
		}));
	};

	return (
		<div className="history-panel">
			{contractStepHistoryList.length > 0 &&
				groupWorkflowSteps(contractStepHistoryList).map(
					({ type, steps }, groupIndex) => (
						<div key={groupIndex}>
							<div className="heading-part">
								<Typography variant="h4">
									{type === "Contract Workflow"
										? `Contract approval Steps - History`
										: type === "Hold Workflow"
										? `Contract Hold Steps - History`
										: type === "Termination Workflow"
										? `Contract Termination Steps - History`
										: type}
								</Typography>
								{steps.length > 0 && (
									<div className="stepper-wrapper">
										<Typography variant="caption">
											{`${steps[0].stepCompletedInPercentage}% Completed`}
										</Typography>
										<Box sx={{ ml: 1, maxWidth: 400, flexGrow: 1 }}>
											<LinearProgress
												variant="determinate"
												value={steps[0].stepCompletedInPercentage}
											/>
										</Box>
									</div>
								)}
							</div>
							<div className="steps-wrapper">
								{!steps.length &&
									(isLoading ? (
										<Loading isWorkflowStepLoader={true} />
									) : (
										<Typography
											className="steps-root"
											variant="body1"
											justifyContent="center"
										>
											No Data Available
										</Typography>
									))}
								{steps.length > 0 &&
									steps.map((item: IContractStepHistory, index: number) => (
										<Link
											key={index}
											onClick={(e) => {
												e.preventDefault();
												handleStepDetailClick(item, index);
											}}
											underline="none"
											className={`${getClassName(item.stepStatus)} ${
												index === activeStepIndex && "active"
											}`}
											id={index === activeStepIndex ? "active" : undefined}
											style={{
												cursor: "pointer",
											}}
										>
											<Tooltip title={item.stepStatus} placement="bottom">
												<div className="requisition-steps-icon">
													{item.stepNumber.includes(".0")
														? item.stepNumber.split(".")[0]
														: item.stepNumber}
												</div>
											</Tooltip>
											<div className="step-content">
												<Typography variant="body2">
													{item.description}
												</Typography>
												<>
													<Typography variant="caption">
														{item.assignToUsers.map((user, index) => (
															<span
																key={index}
																className={
																	user.isTaskCompleted
																		? "green-text fw-500"
																		: ""
																}
															>{`${user.fullName}${
																index !== item.assignToUsers.length - 1
																	? ", "
																	: ""
															}`}</span>
														))}
													</Typography>
													<div className="step-content-middle">
														<div className="left-part">
															<Typography variant="caption" className="light">
																Duration
															</Typography>
															<Typography variant="caption">
																{item.duration || "-"}
															</Typography>
														</div>
														<div className="right-part">
															<Typography variant="caption" className="light">
																Avg. Duration
															</Typography>
															<Typography variant="caption">
																{item.averageDuration || "-"}
															</Typography>
														</div>
													</div>
												</>
												<div className="step-content-bottom">
													<div className="left-part">
														<Tooltip
															title={
																item.completedDate
																	? `Completed by ${item.completedBy}`
																	: ""
															}
															arrow
															placement="top"
														>
															<div>
																<Typography variant="caption" className="light">
																	Date Completed
																</Typography>
																<Typography variant="caption">
																	{item.completedDate
																		? `${Shared.getFormattedDate(
																				new Date(item.completedDate),
																				"dd/MM/yyyy"
																		  )} - ${Shared.getFormattedDate(
																				new Date(item.completedDate),
																				"hh:mmaaa"
																		  )}`
																		: "-"}
																</Typography>
															</div>
														</Tooltip>

														{type === "Hold Workflow" ||
														type === "Termination Workflow" ? (
															<ContractAttachment
																contractId={contractDetail.contractId}
																isDisabled={
																	!contractDetail.contractId ||
																	!contractDetail.contractStepId ||
																	item.stepStatus === ContractStepStatus.Pending
																}
																contractStepId={
																	contractDetail.contractStepId || 0
																}
																isAttachmentViewOnly={
																	isContractStepAssignedToUser(item) &&
																	item.stepStatus === ContractStepStatus.Ready
																		? false
																		: true
																}
																stepNumber={item.stepNumber}
																modulePhaseId={item.modulePhaseId}
															/>
														) : (
															<></>
														)}
													</div>

													{item.comments.length > 0 && (
														<CustomTooltip
															tooltipStyle={{
																border: "1px solid #034f8b",
															}}
															tooltipProps={{
																title: item.comments.map(
																	(cmt: IStepHistoryComment, index: number) => (
																		<Grid
																			container
																			key={index}
																			className={
																				index !== 0 ? "history-comment" : ""
																			}
																		>
																			<Grid item xs={12} className="fw-500">
																				{cmt.fullName}
																			</Grid>
																			<Grid item xs={12} mt={1}>
																				{cmt.comment}
																			</Grid>
																		</Grid>
																	)
																),
																className: "history-tooltip",
																placement: "right",
																children: (
																	<IconButton>
																		<CommentIcon />
																	</IconButton>
																),
															}}
														/>
													)}
												</div>
											</div>
										</Link>
									))}
							</div>
						</div>
					)
				)}

			{openInfoDialog.open && (
				<ResponsiveDialog
					open={openInfoDialog.open}
					title="Step History Info"
					showActionButtons={false}
					titleCloseIcon={false}
					maxWidth="xs"
					onCancel={() => {}}
				>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="h4" color="black">
								{openInfoDialog.message}
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							textAlign="center"
							sx={{ paddingTop: "24px !important" }}
						>
							<Button
								variant="contained"
								onClick={() => {
									setOpenInfoDialog({ open: false, message: "" });
								}}
							>
								Close
							</Button>
						</Grid>
					</Grid>
				</ResponsiveDialog>
			)}
		</div>
	);
};

export default ContractStepsHistory;
